/**
 * Product Categories Constants
 * 
 * This file contains all product category constants used throughout the application.
 */

export enum ProductCategory {
  APP_DEPLOY = 'APP_DEPLOY',
  VM = 'VM',
  BLUEPRINT = 'BLUEPRINT',
  DATABASE = 'DATABASE',
  AI_SERVICE = 'AI_SERVICE',
  SITE = 'SITE',
  RABBITMQ = 'RABBITMQ',
  N8N = 'N8N',
  STRAPI = 'STRAPI',
  MEDUSA = 'MEDUSA',
  ODOO = 'ODOO',
  METABASE = 'METABASE',
  CLICKHOUSE = 'CLICKHOUSE',
  TOOLJET = 'TOOLJET',
  GHOST = 'GHOST',
  REDIS = 'REDIS',
  ONYX = 'ONYX',
  DOCKER = 'DOCKER',
  IDURAR = 'IDURAR',
  FRAPPE_EDUCATION = 'FRAPPE_EDUCATION',
  WORDPRESS = 'WORDPRESS',
  SQLSERVER_EXPRESS = 'SQLSERVER_EXPRESS',
}

/**
 * Array of all product categories
 */
export const PRODUCT_CATEGORIES: ProductCategory[] = [
  ProductCategory.APP_DEPLOY,
  ProductCategory.VM,
  ProductCategory.BLUEPRINT,
  ProductCategory.DATABASE,
  ProductCategory.AI_SERVICE,
  ProductCategory.SITE,
  ProductCategory.RABBITMQ,
  ProductCategory.N8N,
  ProductCategory.STRAPI,
  ProductCategory.MEDUSA,
  ProductCategory.ODOO,
  ProductCategory.METABASE,
  ProductCategory.CLICKHOUSE,
  ProductCategory.TOOLJET,
  ProductCategory.GHOST,
  ProductCategory.REDIS,
  ProductCategory.ONYX,
  ProductCategory.DOCKER,
  ProductCategory.IDURAR,
  ProductCategory.FRAPPE_EDUCATION,
  ProductCategory.WORDPRESS,
  ProductCategory.SQLSERVER_EXPRESS,
];

/**
 * Product category labels for display
 */
export const PRODUCT_CATEGORY_LABELS: Record<ProductCategory, string> = {
  [ProductCategory.APP_DEPLOY]: 'App Deploy',
  [ProductCategory.VM]: 'Virtual Machine',
  [ProductCategory.BLUEPRINT]: 'Blueprint',
  [ProductCategory.DATABASE]: 'Database',
  [ProductCategory.AI_SERVICE]: 'AI Service',
  [ProductCategory.SITE]: 'Site',
  [ProductCategory.RABBITMQ]: 'RabbitMQ',
  [ProductCategory.N8N]: 'n8n',
  [ProductCategory.STRAPI]: 'Strapi CMS',
  [ProductCategory.MEDUSA]: 'Medusa',
  [ProductCategory.ODOO]: 'Odoo ERP/CRM',
  [ProductCategory.METABASE]: 'Metabase BI',
  [ProductCategory.CLICKHOUSE]: 'ClickHouse',
  [ProductCategory.TOOLJET]: 'ToolJet',
  [ProductCategory.GHOST]: 'Ghost CMS',
  [ProductCategory.REDIS]: 'Redis',
  [ProductCategory.ONYX]: 'Onyx',
  [ProductCategory.DOCKER]: 'Docker Host',
  [ProductCategory.IDURAR]: 'IDURAR ERP/CRM',
  [ProductCategory.FRAPPE_EDUCATION]: 'Frappe Education',
  [ProductCategory.WORDPRESS]: 'WordPress',
  [ProductCategory.SQLSERVER_EXPRESS]: 'SQL Server Express 2022',
};

/**
 * Product category descriptions
 */
export const PRODUCT_CATEGORY_DESCRIPTIONS: Record<ProductCategory, string> = {
  [ProductCategory.APP_DEPLOY]: 'Deploy applications and services',
  [ProductCategory.VM]: 'Virtual machine instances',
  [ProductCategory.BLUEPRINT]: 'Pre-configured infrastructure templates',
  [ProductCategory.DATABASE]: 'Managed database services',
  [ProductCategory.AI_SERVICE]: 'AI-powered chatbot assistants',
  [ProductCategory.SITE]: 'Websites powered by AWS Lightsail (WordPress, Drupal, Ghost, etc.)',
  [ProductCategory.RABBITMQ]: 'Managed RabbitMQ message broker instances',
  [ProductCategory.N8N]: 'Managed n8n workflow automation instances',
  [ProductCategory.STRAPI]: 'Managed Strapi headless CMS instances',
  [ProductCategory.MEDUSA]: 'Managed Medusa headless commerce instances',
  [ProductCategory.ODOO]: 'Managed Odoo ERP/CRM instances',
  [ProductCategory.METABASE]: 'Managed Metabase BI & analytics instances',
  [ProductCategory.CLICKHOUSE]: 'Managed ClickHouse columnar analytics database',
  [ProductCategory.TOOLJET]: 'Managed ToolJet low-code internal tools platform',
  [ProductCategory.GHOST]: 'Managed Ghost CMS publishing platform',
  [ProductCategory.REDIS]: 'Managed Redis in-memory database',
  [ProductCategory.ONYX]: 'Managed Onyx enterprise AI search & chat platform',
  [ProductCategory.DOCKER]: 'Bring your own docker-compose.yml — dedicated EC2 with Docker Engine',
  [ProductCategory.IDURAR]: 'Managed IDURAR open-source ERP/CRM instances',
  [ProductCategory.FRAPPE_EDUCATION]: 'Managed Frappe Education ERP instances',
  [ProductCategory.WORDPRESS]: 'Managed WordPress CMS instances with PHP 8.3, MySQL 8, and Nginx',
  [ProductCategory.SQLSERVER_EXPRESS]: 'Managed SQL Server Express 2022 instances on Ubuntu 22.04',
};

/**
 * Helper function to get category label
 */
export const getProductCategoryLabel = (category: ProductCategory): string => {
  return PRODUCT_CATEGORY_LABELS[category] || category;
};

/**
 * Helper function to get category description
 */
export const getProductCategoryDescription = (category: ProductCategory): string => {
  return PRODUCT_CATEGORY_DESCRIPTIONS[category] || '';
};

/**
 * Helper function to check if a category is valid
 */
export const isValidProductCategory = (category: string): category is ProductCategory => {
  return PRODUCT_CATEGORIES.includes(category as ProductCategory);
};

